import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';

/**
 * Bootstrap 5 Breakpoints
 * https://getbootstrap.com/docs/5.3/layout/breakpoints/
 */
export const BREAKPOINTS = {
  xs: 0,      // Extra small devices (portrait phones, <576px)
  sm: 576,    // Small devices (landscape phones, ≥576px)
  md: 768,    // Medium devices (tablets, ≥768px)
  lg: 992,    // Large devices (desktops, ≥992px)
  xl: 1200,   // Extra large devices (large desktops, ≥1200px)
  xxl: 1400,  // Extra extra large devices (larger desktops, ≥1400px)
} as const;

/**
 * Service that adds responsive breakpoint classes to the window.document body.
 * Classes follow Bootstrap naming: screen-xs, screen-sm, screen-md, screen-lg, screen-xl, screen-xxl
 * Also adds orientation classes: orientation-portrait, orientation-landscape
 *
 * Components can use :host-context() to apply styles based on screen size:
 *
 * @example
 * ```css
 * :host-context(.screen-xs) .my-element {
 *   display: none;
 * }
 *
 * :host-context(.screen-md) .my-element {
 *   display: block;
 * }
 *
 * :host-context(.screen-xs):host-context(.orientation-landscape) .photo {
 *   max-width: 90vw;
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class BreakpointService {
  private readonly classes = {
    xs: 'screen-xs',
    sm: 'screen-sm',
    md: 'screen-md',
    lg: 'screen-lg',
    xl: 'screen-xl',
    xxl: 'screen-xxl',
  };

  constructor(
    private breakpointObserver: BreakpointObserver,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.initBreakpointObservers();
    this.initOrientationObserver();
  }

  private initBreakpointObservers(): void {
    // Watch each breakpoint and add/remove corresponding class
    this.breakpointObserver
      .observe(`(min-width: ${BREAKPOINTS.xxl}px)`)
      .subscribe((result) => this.updateClass('xxl', result.matches));

    this.breakpointObserver
      .observe(`(min-width: ${BREAKPOINTS.xl}px) and (max-width: ${BREAKPOINTS.xxl - 0.02}px)`)
      .subscribe((result) => this.updateClass('xl', result.matches));

    this.breakpointObserver
      .observe(`(min-width: ${BREAKPOINTS.lg}px) and (max-width: ${BREAKPOINTS.xl - 0.02}px)`)
      .subscribe((result) => this.updateClass('lg', result.matches));

    this.breakpointObserver
      .observe(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 0.02}px)`)
      .subscribe((result) => this.updateClass('md', result.matches));

    this.breakpointObserver
      .observe(`(min-width: ${BREAKPOINTS.sm}px) and (max-width: ${BREAKPOINTS.md - 0.02}px)`)
      .subscribe((result) => this.updateClass('sm', result.matches));

    this.breakpointObserver
      .observe(`(max-width: ${BREAKPOINTS.sm - 0.02}px)`)
      .subscribe((result) => this.updateClass('xs', result.matches));
  }

  private initOrientationObserver(): void {
    // Watch for orientation changes
    this.breakpointObserver
      .observe('(orientation: portrait)')
      .subscribe((result) => {
        if (result.matches) {
          this.document.body.classList.add('orientation-portrait');
          this.document.body.classList.remove('orientation-landscape');
        }
      });

    this.breakpointObserver
      .observe('(orientation: landscape)')
      .subscribe((result) => {
        if (result.matches) {
          this.document.body.classList.add('orientation-landscape');
          this.document.body.classList.remove('orientation-portrait');
        }
      });
  }

  private updateClass(
    breakpoint: keyof typeof this.classes,
    matches: boolean
  ): void {
    const className = this.classes[breakpoint];
    if (matches) {
      this.document.body.classList.add(className);
    } else {
      this.document.body.classList.remove(className);
    }
  }

  /**
   * Check if current screen is at or above a specific breakpoint
   */
  isMinWidth(breakpoint: keyof typeof BREAKPOINTS): boolean {
    return this.breakpointObserver.isMatched(`(min-width: ${BREAKPOINTS[breakpoint]}px)`);
  }

  /**
   * Check if current screen is below a specific breakpoint
   */
  isMaxWidth(breakpoint: keyof typeof BREAKPOINTS): boolean {
    return this.breakpointObserver.isMatched(`(max-width: ${BREAKPOINTS[breakpoint] - 0.02}px)`);
  }
}
